import React, { useState } from 'react'
import styled from 'styled-components'
import { Button, Input, Select } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { get } from 'lodash'
import { askAIAboutClient } from '../../api'
import { buildAIHistory, normalizeAIResponse } from '../../utils/askAI'

const { Option } = Select

const AskAIFloatingButton = styled(Button)`
  position: fixed;
  right: 24px;
  bottom: 92px;
  z-index: 1000;
  border-radius: 999px;
  box-shadow: 0px 8px 24px rgba(38, 66, 130, 0.25);
`

const AskAIPanel = styled.div`
  position: fixed;
  top: 64px;
  right: 0;
  width: 420px;
  max-width: 100%;
  height: calc(100vh - 64px);
  background: #ffffff;
  border-left: 1px solid #e5eaf0;
  box-shadow: -8px 0px 24px rgba(50, 63, 74, 0.12);
  z-index: 1001;
  display: flex;
  flex-direction: column;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid #e5eaf0;
    color: #264382;
    font-weight: 600;
  }

  .messages {
    flex: 1;
    overflow-y: auto;
    padding: 14px 16px;
    background: #f8fafc;
  }

  .msg {
    margin-bottom: 12px;
    max-width: 90%;
    padding: 10px 12px;
    border-radius: 12px;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 13px;
    line-height: 1.5;
  }

  .msg.user {
    margin-left: auto;
    background: #264382;
    color: #fff;
    border-bottom-right-radius: 4px;
  }

  .msg.ai {
    margin-right: auto;
    background: #fff;
    color: #334e68;
    border: 1px solid #e5eaf0;
    border-bottom-left-radius: 4px;
  }

  .msg .msg-image {
    display: block;
    margin-top: 10px;
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e5eaf0;
  }

  .msg .msg-image-error {
    padding: 8px 10px;
    font-size: 12px;
    color: #b44b4b;
    background: #fff6f6;
    border: 1px dashed #e6b3b3;
  }

  .msg .msg-image-src {
    display: block;
    margin-top: 4px;
    word-break: break-all;
    color: #829ab1;
  }

  .composer {
    border-top: 1px solid #e5eaf0;
    padding: 12px;
    background: #fff;
  }

  .composer-actions {
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .composer-mode {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #627d98;
    user-select: none;
  }

  .composer-mode .ant-select {
    min-width: 96px;
  }
`

/**
 * Floating "Ask AI" button + side chat panel. Sends the prompt to the AI
 * sandbox agent together with the client id and the chat history; the agent
 * answers with text and/or a generated image.
 *
 * Props:
 *  - person: the client object (must expose `id`), sent as `client_id`
 *  - greeting: first message shown in the empty conversation
 *  - textPlaceholder / imagePlaceholder: composer placeholders per mode
 *  - defaultGenerateImage: initial response mode (image vs text)
 */
const AskAI = ({
  person,
  greeting = 'Hi! Ask me anything about this client.',
  textPlaceholder = "Ask anything about this client's health data, reports, or trends...",
  imagePlaceholder = "Describe the image you want generated from this client's data...",
  defaultGenerateImage = true
}) => {
  const [isAIChatOpen, setIsAIChatOpen] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  // Tells the AI sandbox whether this question expects an image or text.
  const [aiGenerateImage, setAiGenerateImage] = useState(
    Boolean(defaultGenerateImage)
  )
  const [aiMessages, setAiMessages] = useState([
    {
      role: 'ai',
      text: greeting,
      greeting: true
    }
  ])
  // Images that resolved to a src the browser refused to load (bad host, 401 on
  // an auth protected url, expired presigned link...). Surfaced in the bubble so
  // the failing src is visible instead of a silently blank area.
  const [aiImageErrors, setAiImageErrors] = useState({})

  const onAIImageError = (key, src) => {
    console.warn('[AskAI] image failed to load, src =', src)
    setAiImageErrors((prev) => ({ ...prev, [key]: true }))
  }

  const onAskAI = async () => {
    const prompt = (aiPrompt || '').trim()
    if (!prompt || aiLoading) return

    const history = buildAIHistory(aiMessages)

    setAiMessages((prev) => [...prev, { role: 'user', text: prompt }])
    setAiPrompt('')
    setAiLoading(true)
    try {
      const result = await askAIAboutClient({
        prompt,
        client_id: get(person, 'id'),
        history,
        generate_image: aiGenerateImage
      })

      const normalized = normalizeAIResponse(result)
      console.info(
        '[AskAI] answer images extracted:',
        normalized.imageUrls.length,
        '| payload keys:',
        Object.keys(result || {})
      )
      setAiMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          text: normalized.text,
          imageUrl: normalized.imageUrl,
          imageUrls: normalized.imageUrls
        }
      ])
    } catch (error) {
      const detail = get(error, 'detail')
      setAiMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          text: detail
            ? `Sorry — ${detail}`
            : 'Sorry, I could not fetch an answer right now. Please try again.'
        }
      ])
    } finally {
      setAiLoading(false)
    }
  }

  return (
    <>
      <AskAIFloatingButton
        type="primary"
        onClick={() => setIsAIChatOpen(true)}
      >
        Ask AI
      </AskAIFloatingButton>
      {isAIChatOpen && (
        <AskAIPanel>
          <div className="header">
            <span>Ask AI</span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setIsAIChatOpen(false)}
            />
          </div>
          <div className="messages">
            {aiMessages.map((item, index) => {
              const images =
                item.imageUrls && item.imageUrls.length
                  ? item.imageUrls
                  : item.imageUrl
                  ? [item.imageUrl]
                  : []

              return (
                <div className={`msg ${item.role}`} key={`ai-msg-${index}`}>
                  {item.text}
                  {images.map((src, imgIndex) => {
                    const imageKey = `ai-msg-${index}-img-${imgIndex}`

                    if (aiImageErrors[imageKey]) {
                      return (
                        <div className='msg-image msg-image-error' key={imageKey}>
                          Image could not be loaded
                          <span className='msg-image-src'>
                            {src.slice(0, 160)}
                          </span>
                        </div>
                      )
                    }

                    return (
                      <img
                        src={src}
                        alt='AI generated visual'
                        className='msg-image'
                        key={imageKey}
                        onError={() => onAIImageError(imageKey, src)}
                      />
                    )
                  })}
                </div>
              )
            })}
          </div>
          <div className="composer">
            <Input.TextArea
              value={aiPrompt}
              autoSize={{ minRows: 3, maxRows: 5 }}
              placeholder={aiGenerateImage ? imagePlaceholder : textPlaceholder}
              onChange={(e) => setAiPrompt(e.target.value)}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault()
                  onAskAI()
                }
              }}
            />
            <div className="composer-actions">
              <div className="composer-mode">
                <span>Response</span>
                <Select
                  size='small'
                  value={aiGenerateImage ? 'image' : 'text'}
                  disabled={aiLoading}
                  // The composer sits at the very bottom of the fixed Ask AI
                  // panel, so open the dropdown upwards to keep it on screen.
                  placement='topLeft'
                  onChange={(value) => setAiGenerateImage(value === 'image')}
                >
                  <Option value='image' label='Image'>
                    Image
                  </Option>
                  <Option value='text' label='Text'>
                    Text
                  </Option>
                </Select>
              </div>
              <Button type="primary" loading={aiLoading} onClick={onAskAI}>
                Send
              </Button>
            </div>
          </div>
        </AskAIPanel>
      )}
    </>
  )
}

export default AskAI
